import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, CheckCircle, XCircle, Clock, Calendar, ClipboardList, Loader2, AlertCircle } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateActionDialog } from "@/components/crm/CreateActionDialog";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface Action {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_at: string | null;
  assigned_to: string | null;
  family_id: string | null;
  contact_id: string | null;
  created_at: string;
  families?: { name: string } | null;
  contacts?: { full_name: string } | null;
}

export default function Actions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId, familyId, isMFO } = useTenant();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Query all actions for the org (or family if SFO)
  const { data: actions = [], isLoading } = useQuery({
    queryKey: ["all-actions", orgId, familyId, isMFO],
    queryFn: async () => {
      let query = supabase
        .from("actions")
        .select(`
          *,
          families:family_id (name),
          contacts:contact_id (full_name)
        `)
        .eq("org_id", orgId);

      // For SFO, filter by the current family
      if (!isMFO && familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query.order("due_at", { ascending: true });
      if (error) throw error;
      return (data || []) as Action[];
    },
    enabled: !!orgId,
  });

  const filteredActions = useMemo(() => {
    return actions.filter((action: Action) => {
      const matchesSearch =
        action.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        action.description?.toLowerCase().includes(searchQuery.toLowerCase());

      if (filter === "all") return matchesSearch;
      if (filter === "overdue") {
        return matchesSearch && action.due_at && new Date(action.due_at) < new Date() && action.status !== "completed";
      }
      return matchesSearch && action.status === filter;
    });
  }, [actions, searchQuery, filter]);

  const stats = useMemo(() => ({
    pending: actions.filter((a: Action) => a.status === "pending").length,
    open: actions.filter((a: Action) => a.status === "open").length,
    overdue: actions.filter((a: Action) => a.due_at && new Date(a.due_at) < new Date() && a.status !== "completed").length,
    completed: actions.filter((a: Action) => a.status === "completed").length,
  }), [actions]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case "open":
        return <Badge variant="secondary">Open</Badge>;
      case "completed":
        return <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const isOverdue = (action: Action) => {
    return action.due_at && new Date(action.due_at) < new Date() && action.status !== "completed";
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Actions</h1>
          <p className="text-muted-foreground">
            Track and manage action items across your organization
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Create Action
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card
          className={`cursor-pointer hover:bg-accent/50 ${filter === "pending" ? "border-primary" : ""}`}
          onClick={() => setFilter(filter === "pending" ? "all" : "pending")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">Awaiting action</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer hover:bg-accent/50 ${filter === "open" ? "border-primary" : ""}`}
          onClick={() => setFilter(filter === "open" ? "all" : "open")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.open}</div>
            <p className="text-xs text-muted-foreground">In progress</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer hover:bg-accent/50 ${filter === "overdue" ? "border-primary" : ""}`}
          onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.overdue}</div>
            <p className="text-xs text-muted-foreground">Needs attention</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer hover:bg-accent/50 ${filter === "completed" ? "border-primary" : ""}`}
          onClick={() => setFilter(filter === "completed" ? "all" : "completed")}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
            <p className="text-xs text-muted-foreground">Done</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Action Items</CardTitle>
              <CardDescription>
                Manage and track all outstanding action items
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search actions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-[250px]"
                />
              </div>
              {filter !== "all" && (
                <Button variant="ghost" size="sm" onClick={() => setFilter("all")}>
                  Clear filter
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredActions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Due Date</TableHead>
                  {isMFO && <TableHead>Family</TableHead>}
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActions.map((action: Action) => (
                  <TableRow
                    key={action.id}
                    className={isOverdue(action) ? "bg-destructive/5" : ""}
                  >
                    <TableCell>
                      <div className="max-w-[300px]">
                        <p className="font-medium truncate">{action.title}</p>
                        {action.description && (
                          <p className="text-xs text-muted-foreground truncate">{action.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getPriorityColor(action.priority) as "default" | "secondary" | "destructive" | "outline"}>
                        {action.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {isOverdue(action) ? (
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        ) : (
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className={isOverdue(action) ? "text-destructive font-medium" : ""}>
                          {formatDate(action.due_at)}
                        </span>
                      </div>
                    </TableCell>
                    {isMFO && (
                      <TableCell>
                        {action.families?.name ? (
                          <Button
                            variant="link"
                            className="p-0 h-auto text-primary"
                            onClick={() => navigate(`/firm/families/${action.family_id}`)}
                          >
                            {action.families.name}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {action.contacts?.full_name || <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>{getStatusBadge(action.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {action.status !== "completed" && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">View</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Actions</h3>
              <p className="text-muted-foreground max-w-sm mb-4">
                {searchQuery || filter !== "all"
                  ? "No actions match your current filters."
                  : "Action items will appear here when created manually or proposed by the AI copilot."}
              </p>
              {(searchQuery || filter !== "all") && (
                <Button variant="outline" onClick={() => { setSearchQuery(""); setFilter("all"); }}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateActionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["all-actions"] });
        }}
      />
    </div>
  );
}
